import type { TFunction } from '@mukil/i18n';
import {
  FormField,
  MoneyInput,
  StyledSelect,
  Switch,
} from '@jumpstone/ui-react';
import {
  isApitBorneUnavailable,
  type EmployeeFormDraft,
  type EmployeeFormErrors,
} from './employee_form_validation';
import { SELECT_TRIGGER_CLASSES } from './employee_form_fields';

export interface EmployeeFormLkTaxFieldsProps {
  draft: EmployeeFormDraft;
  errors: EmployeeFormErrors;
  t: TFunction;
  set: <K extends keyof EmployeeFormDraft>(
    key: K,
    value: EmployeeFormDraft[K],
  ) => void;
}

/**
 * Sri Lanka tax status (#26653 residency + citizenship, ADR-168 APIT
 * employment): which IRD table withholds this employee's APIT. Pure
 * presentation; the caller hides it for a contractor (not on payroll), and the
 * pay-routing rules live in the API (`computeStatutoryDeductions`), never here.
 */
export function EmployeeFormLkTaxFields({
  draft,
  errors,
  t,
  set,
}: EmployeeFormLkTaxFieldsProps): React.JSX.Element {
  const secondary = draft.apit_employment_type === 'secondary';
  const nonResidentNonCitizen =
    draft.residency_status === 'non_resident' && !draft.is_citizen;
  const borneUnavailable = isApitBorneUnavailable(draft);
  return (
    <>
      <FormField label={t('employees.form_residency_status')}>
        <StyledSelect
          value={draft.residency_status}
          onChange={(value) => {
            set('residency_status', value);
          }}
          options={[
            {
              value: 'resident',
              label: t('employees.form_residency_resident'),
            },
            {
              value: 'non_resident',
              label: t('employees.form_residency_non_resident'),
            },
          ]}
          ariaLabel={t('employees.form_residency_status')}
          triggerClassName={SELECT_TRIGGER_CLASSES}
        />
      </FormField>

      <FormField
        label={t('employees.form_is_citizen')}
        helpText={
          nonResidentNonCitizen
            ? t('employees.form_non_resident_non_citizen_hint')
            : undefined
        }
      >
        <StyledSelect
          value={draft.is_citizen ? 'yes' : 'no'}
          onChange={(value) => {
            set('is_citizen', value === 'yes');
          }}
          options={[
            { value: 'yes', label: t('employees.form_is_citizen_yes') },
            { value: 'no', label: t('employees.form_is_citizen_no') },
          ]}
          ariaLabel={t('employees.form_is_citizen')}
          triggerClassName={SELECT_TRIGGER_CLASSES}
        />
      </FormField>

      <FormField
        label={t('employees.form_apit_employment_type')}
        helpText={
          secondary
            ? t('employees.form_apit_employment_hint_secondary')
            : t('employees.form_apit_employment_hint_primary')
        }
      >
        <StyledSelect
          value={draft.apit_employment_type}
          onChange={(value) => {
            set('apit_employment_type', value);
          }}
          options={[
            {
              value: 'primary',
              label: t('employees.form_apit_employment_primary'),
            },
            {
              value: 'secondary',
              label: t('employees.form_apit_employment_secondary'),
            },
          ]}
          ariaLabel={t('employees.form_apit_employment_type')}
          triggerClassName={SELECT_TRIGGER_CLASSES}
        />
      </FormField>

      {secondary && (
        <FormField
          label={t('employees.form_apit_declared_primary_income')}
          htmlFor="employee-form-apit-declared-primary-income"
          helpText={t('employees.form_apit_declared_primary_income_hint')}
          error={
            errors.apit_declared_primary_income_cents &&
            t('employees.form_error_negative_amount')
          }
        >
          <MoneyInput
            id="employee-form-apit-declared-primary-income"
            value={draft.apit_declared_primary_income_cents ?? 0}
            blankZero
            onChange={(cents) => {
              set(
                'apit_declared_primary_income_cents',
                cents === 0 ? null : cents,
              );
            }}
          />
        </FormField>
      )}

      <FormField
        label={t('employees.form_apit_borne_by_employer')}
        helpText={
          borneUnavailable
            ? t('employees.form_apit_borne_unavailable_hint')
            : t('employees.form_apit_borne_by_employer_hint')
        }
      >
        <Switch
          checked={draft.apit_borne_by_employer && !borneUnavailable}
          disabled={borneUnavailable}
          onCheckedChange={(checked) => {
            set('apit_borne_by_employer', checked);
          }}
          aria-label={t('employees.form_apit_borne_by_employer')}
        />
      </FormField>
    </>
  );
}
